import { useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface Point {
	x: number;
	y: number;
}

interface FlyingLetterProps {
	id: string;
	letter: string;
	letterIndex: number;
	from: Point;
	to: Point;
	duration?: number;
	onComplete: (id: string, letter: string, letterIndex: number) => void;
}

const FlyingLetter = ({ id, letter, letterIndex, from, to, duration = 550, onComplete }: FlyingLetterProps) => {
	const elementRef = useRef<HTMLDivElement | null>(null);
	const [halfSize, setHalfSize] = useState({ width: 0, height: 0 });
	const deltaX = to.x - from.x;
	const deltaY = to.y - from.y;
	const durationSeconds = duration / 1000;

	useLayoutEffect(() => {
		const node = elementRef.current;
		if (!node) return;

		const { width, height } = node.getBoundingClientRect();
		const nextHalfSize = { width: width / 2, height: height / 2 };

		setHalfSize((prev) =>
			prev.width === nextHalfSize.width && prev.height === nextHalfSize.height ? prev : nextHalfSize,
		);
	}, [id, letter]);

	useLayoutEffect(() => {
		const node = elementRef.current;
		if (!node) return;

		const ctx = gsap.context(() => {
			gsap.set(node, {
				x: 0,
				y: 0,
				opacity: 0,
				scale: 0.65,
				transformOrigin: 'center center',
			});

			gsap.to(node, {
				x: deltaX,
				y: deltaY,
				opacity: 1,
				scale: 1,
				duration: durationSeconds,
				ease: 'power2.out',
				onComplete: () => onComplete(id, letter, letterIndex),
			});
		}, node);

		return () => {
			ctx.revert();
		};
	}, [deltaX, deltaY, durationSeconds, id, letter, letterIndex, onComplete]);

	return (
		<div
			ref={elementRef}
			className="letter-party-game__flying-letter"
			style={{
				left: from.x - halfSize.width,
				top: from.y - halfSize.height,
			}}
			aria-hidden
		>
			<span>{letter.toLowerCase()}</span>
		</div>
	);
};

export default FlyingLetter;
